import React, { useEffect, useState } from 'react';

import { UserResponse } from './types';

import { API, ResponseStatus, useFetch } from 'app/api';
import { FreshchatContext } from 'app/lib/freshchat/FreshchatContext';
import { freshchatSetProperty } from 'app/lib/freshchat/freshchatSetProperty';

const isSSR = typeof window === 'undefined';

type InitialUserData = {
  clientId: string;
  restoreId: string;
};

type FreshchatInitProps = {
  token?: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  host?: string;
  email?: string;
  phone?: string;
  phoneCountryCode?: string;
  config?: Record<string, string | boolean | Record<string, string | boolean>>;
  open?: boolean;
  tags?: [string];
  faqTags?: string[];
  locale?: string;
  initialUserData: InitialUserData | null;
};

type FreshchatProps = FreshchatInitProps & {
  label?: string;
  icStyles?: React.CSSProperties;
};

const id = 'freshchat-lib';

const FreshchatProvider = <U extends object>({
  label,
  icStyles,
  children,
  initialUserData,
  ...rest
}: React.PropsWithChildren<FreshchatProps>): JSX.Element => {
  const [isChatReady, setChatReady] = useState(false);
  const [userData, setUserData] = useState<Partial<U>>();
  const [unreadCount, setUnreadCount] = useState(0);

  const { makeRequest: saveRestoreId } = useFetch(
    API.freshChat.sendFreshChatRestoreId,
    { skipSSR: true },
  );

  const isWidgetOpen = () => {
    return window?.fcWidget?.isOpen();
  };

  const handleUserCreationResponse = () => {
    window?.fcWidget?.on('user:created', function (resp: UserResponse) {
      const status = resp && resp.status;

      if (status === ResponseStatus.SUCCESS) {
        const { restoreId, alias }  = resp.data;
        if (restoreId && alias){
          saveRestoreId({
            restoreId,
            externalUserId: alias,
          });
        }
      }
    });
  };

  const handleUnreadMessages = () => {
    window?.fcWidget?.on('unreadCount:notify', (response: { count: number }) => {
      setUnreadCount(response.count);
    });
  };

  const init = () => {
    if (rest.config) {
      window?.fcWidget?.init({
        ...rest,
        ...initialUserData,
      });

      handleUnreadMessages();

      if (initialUserData?.clientId){
        handleUserCreationResponse();
      }
    }
  };

  const onLoadHandler = function () {
    try {
      init();
    }
    catch (error) {
      console.error(error);
    }

    try {
      window?.fcWidget?.on('widget:loaded', () => {
        setChatReady(true);
      });
    }
    catch (error) {
      console.error(error);
    }
  };

  const loadScript = async (host: string) => {
    if (document.getElementById(id) && window.fcWidget) {
      return;
    }
    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.src = `${host}/js/widget.js`;
    script.id = id;
    // ADD
    script.onload = onLoadHandler;
    // ADD
    document.head.appendChild(script);
  };

  const handleWidgetDestroyed = (onWidgetReload: () => void) => {
    window.fcWidget?.on('widget:destroyed', function () {
      onWidgetReload();
    });
  };

  const unloadFreshchat = async () => {
    if (window?.fcWidget){
      handleWidgetDestroyed(onLoadHandler);

      window.fcWidget.close();
      window.fcWidget.user.clear();
      window.fcWidget.destroy();
    }
  };

  const menuToggleHandler = () => {
    if (isWidgetOpen()) {
      window?.fcWidget?.close();
    }
    else {
      window?.fcWidget?.open();
    }
  };

  const handleReload = async (host: string) => {
    await unloadFreshchat();
    await loadScript(host);
  };

  useEffect(() => {
    if (!rest.host) {
      console.error(`Host for Freshchat script.src from rest.host=${rest.host}`);
    }

    if (!isSSR && rest.host) {
      handleReload(rest.host).catch((error) => console.error(error));
    }
  }, [initialUserData?.clientId, rest.host]);

  useEffect(() => {
    if (!isSSR && isChatReady && window?.fcWidget) {
      const dataForProperty = {
        ...initialUserData,
        ...userData,
      };

      freshchatSetProperty('user', dataForProperty);
    }
  }, [isChatReady, userData, initialUserData]);

  const providerValue = {
    isChatReady,
    setUserData,
    menuToggleHandler,
    unreadCount,
  };

  return (
    <FreshchatContext.Provider value={providerValue}>
      {children}
    </FreshchatContext.Provider>
  );
};

export default FreshchatProvider;

export { useFreshchatContext as useFreshchat } from './FreshchatContext';
